import React from 'react';
import { type FieldValues, type Path, type PathValue } from 'react-hook-form';

import { useFormElement } from '../useFormElement';

import { BaseSelectInput, type BaseSelectInputProps } from './BaseSelectInput';

export type SelectInputProps<K, T extends FieldValues, FP extends Path<T>> = Omit<
	BaseSelectInputProps<K>,
	'hasError' | 'name' | 'onChange' | 'ref' | 'value'
> & {
	defaultValue?: PathValue<T, FP>;
	name: FP;
	onValueChange?: (value: K) => void;
};

/**
 *
 * @param {SelectInputProps} props - The component's props
 * @param {string} label - label to display
 * @param {string} children - use this in case label is not enough
 * @param {string} defaultChecked - initial checked state
 * @param {string} disabled - is the checkbox disabled
 * @param {boolean} isOnError - can be used to activate the on-error style
 * @param {function} onChange - on box click change callback
 */
export function SelectInput<K, T extends FieldValues, FP extends Path<T> = Path<T>>({
	defaultValue,
	name,
	onValueChange,
	...props
}: SelectInputProps<K, T, FP>) {
	const {
		field: { onChange: onFormElementChange, value },
		fieldState: { error }
	} = useFormElement<T, FP>({
		defaultValue,
		name
	});
	const onChange = (value: K) => {
		onFormElementChange(value);
		onValueChange?.(value);
	};

	return <BaseSelectInput error={error} value={value} onChange={onChange} {...props} />;
}
